import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SiFigma,
  SiReact,
  SiHtml5,
  SiCss3,
  SiJavascript,
  SiTypescript,
  SiTailwindcss,
  SiNodedotjs,
  SiExpress,
  SiDocker,
  SiGit,
  SiGithub,
} from "react-icons/si";

import Hero from "../components/Hero.tsx";

const Home = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");

  const navigate = useNavigate();

  const plans = [
    {
      title: "Basic Branded Website",
      description:
        "Perfect for startups or small businesses needing an online presence.",
      price: { monthly: 50, yearly: 500 },
      features: [
        "Up to 5 pages",
        "Responsive design",
        "SEO-optimised",
        "Custom branding",
        "1-year hosting",
      ],
      highlighted: false,
    },
    {
      title: "Cloud Solutions",
      description:
        "Best for businesses needing secure storage and collaboration tools.",
      price: { monthly: 80, yearly: 800 },
      features: [
        "500-1TB cloud storage",
        "File sharing and backup",
        "24/7 support",
        "Scalable infrastructure",
        "Custom configurations",
      ],
      highlighted: true,
    },
    {
      title: "Small-Scale Application",
      description:
        "Ideal for custom tools or applications tailored to your business needs.",
      price: { monthly: 250, yearly: 2200 },
      features: [
        "Custom-built application",
        "User-friendly UI/UX",
        "Database integration",
        "Scalable and lightweight",
        "Dedicated support",
      ],
      highlighted: false,
    },
  ];

  const techStack = [
    { name: "Figma", icon: <SiFigma size={32} color="#F24E1E" /> },
    { name: "React", icon: <SiReact size={32} color="#61DAFB" /> },
    { name: "HTML", icon: <SiHtml5 size={32} color="#E34F26" /> },
    { name: "CSS", icon: <SiCss3 size={32} color="#1572B6" /> },
    { name: "JavaScript", icon: <SiJavascript size={32} color="#F7DF1E" /> },
    { name: "TypeScript", icon: <SiTypescript size={32} color="#3178C6" /> },
    { name: "TailwindCSS", icon: <SiTailwindcss size={32} color="#06B6D4" /> },
    { name: "NodeJS", icon: <SiNodedotjs size={32} color="#339933" /> },
    { name: "Express", icon: <SiExpress size={32} color="#000000" /> },
    { name: "Docker", icon: <SiDocker size={32} color="#2496ED" /> },
    { name: "Git", icon: <SiGit size={32} color="#F05032" /> },
    { name: "GitHub", icon: <SiGithub size={32} color="#181717" /> },
  ];

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-screen">
      <Hero />

      <section className="container mx-auto px-4 py-10">
        <h2 className="text-3xl font-bold text-center mb-6">Our Popular Services</h2>
        <div className="flex justify-center mb-8">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-2 rounded-l-lg ${
              billingCycle === "monthly"
                ? "bg-brandcolour text-black"
                : "bg-gray-200 dark:bg-gray-700 text-black dark:text-white"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`px-4 py-2 rounded-r-lg ${
              billingCycle === "yearly"
                ? "bg-brandcolour text-black"
                : "bg-gray-200 dark:bg-gray-700 text-black dark:text-white"
            }`}
          >
            Yearly
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`border rounded-lg p-6 ${
                plan.highlighted
                  ? "bg-gray-900 text-white border-brandcolour"
                  : "bg-white dark:bg-gray-800 dark:text-gray-300 border-gray-300"
              }`}
            >
              <h3 className="text-xl font-bold mb-2 text-center">
                {plan.title}
              </h3>
              <p className="text-sm text-center mb-4">{plan.description}</p>
              <div className="text-center">
                <span className="text-4xl font-bold">
                  £{plan.price[billingCycle]}
                </span>
                <span className="text-lg font-medium">
                  {billingCycle === "monthly" ? " /mo" : " annually"}
                </span>
              </div>
              <ul className="mt-4 space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="text-sm">
                    • {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/services")}
                className={`mt-6 w-full py-2 px-4 rounded-lg ${
                  plan.highlighted
                    ? "bg-gray-200 text-gray-900"
                    : "bg-gray-900 text-white dark:bg-gray-700"
                }`}
              >
                Find out More!
              </button>
            </div>
          ))}
        </div>
        <div className="mt-6 text-sm text-center text-gray-600 dark:text-gray-400">
          <strong>Disclaimer:</strong> All prices are estimates and subject to
          change based on project size and complexity. Development time may also
          impact pricing.
        </div>
      </section>

      <section className="py-10">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-black dark:text-white">
            Partnering with The Digital Way
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            At{" "}
            <span className="text-brandcolour font-semibold">
              HaZe IT Solutions
            </span>
            , we are proud to collaborate with{" "}
            <span className="text-brandcolour font-semibold">
              The Digital Way
            </span>{" "}
            to deliver comprehensive solutions that go beyond website creation.
            Together, we offer services that combine exceptional design,
            strategic marketing, and innovative branding to help your business
            thrive online.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            By combining our expertise in web development with The Digital Way's
            focus on marketing and brand development, we ensure your digital
            presence not only attracts attention but also drives engagement and
            results. Whether you need a stunning website, a solid marketing
            strategy, or a powerful brand identity, this partnership has you
            covered.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            With this collaboration, you'll benefit from:
          </p>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 text-left mx-auto max-w-2xl">
            <li>
              <span className="font-semibold">Enhanced Digital Presence:</span>{" "}
              Websites that look stunning and perform seamlessly.
            </li>
            <li>
              <span className="font-semibold">Strategic Marketing:</span>{" "}
              Tailored marketing strategies to maximize your reach.
            </li>
            <li>
              <span className="font-semibold">Comprehensive Branding:</span>{" "}
              Build a unique, memorable identity for your business.
            </li>
            <li>
              <span className="font-semibold">Data-Driven Decisions:</span>{" "}
              Detailed performance monitoring and reporting to guide your
              growth.
            </li>
            <li>
              <span className="font-semibold">Expert Collaboration:</span> A
              seamless partnership that handles everything from design to
              delivery.
            </li>
          </ul>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
            {[
              "Marketing Strategy Development",
              "Digital Marketing",
              "Content Marketing",
              "Brand Development",
              "Market Research and Analysis",
              "Performance Monitoring and Reporting",
            ].map((service, index) => (
              <div
                key={index}
                className="p-4 bg-white dark:bg-gray-800 border dark:border-brandcolour rounded-lg shadow"
              >
                <h3 className="text-lg font-bold text-brandcolour">
                  {service}
                </h3>
              </div>
            ))}
          </div>
          <a
            href="https://thedigitalway.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-6 px-6 py-3 bg-brandcolour text-white dark:text-black font-semibold rounded-lg shadow-md hover:bg-purple-700"
            aria-label="Learn more about our partnership with The Digital Way"
          >
            Learn more about The Digital Way!
          </a>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6 text-center">My Tech Stack</h2>
        <div className="overflow-hidden relative">
          <div className="flex animate-scroll space-x-4 md:space-x-6">
            {Array(3)
              .fill(techStack)
              .flat()
              .map((tech, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center shrink-0 w-[60px] h-[60px] md:w-[80px] md:h-[80px] mx-2 md:mx-4 bg-gray-200 dark:bg-gray-800 rounded-full shadow-lg p-2 transition-transform transform hover:scale-110"
                >
                  {tech.icon}
                  <span className="text-xs md:text-sm mt-2 text-gray-700 dark:text-gray-300">
                    {tech.name}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
